'use client';
import { useSwiperSlide } from 'swiper/react';
import { memo, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Center, VStack, Text, HStack } from '@chakra-ui/react';

import CustomOverlayLink from 'shared/CustomOverlayLink';
import AnimatedText from 'shared/AnimatedText';
import NextImage from 'shared/NextImage';

import Pdf from 'assets/icons/pdf.svg';
import { reviewsPdfLinkText } from 'constants/Reviews';
import { DEFAULT_BORDER_RADIUS } from 'constants/global';

const ReviewCard = ({
  companyIcon,
  text,
  textProps = null,
  name,
  nameProps,
  position,
  positionProps = null,
  textsContainerProps = null,
  iconBorder = false,
  iconProps,
  pdfLink = null,
  controlIsViewed = false,
  ...props
}) => {
  const swiperSlide = useSwiperSlide();
  const { ref, inView } = useInView({ threshold: 0, triggerOnce: true });
  const [isViewed, setIsViewed] = useState(controlIsViewed);

  useEffect(() => {
    if (!isViewed && swiperSlide?.isActive && inView) setIsViewed(true);
  }, [swiperSlide?.isActive, inView]);

  return (
    <VStack
      ref={ref}
      pos="relative"
      w={{ mobile: 'full', xm: '343px', tablet: '520px', laptop: '700px' }}
      h="full"
      minH={{ mobile: '400px', tablet: '310px', laptop: '430px' }}
      p={{ mobile: '60px 24px 24px', laptop: '100px 40px 40px' }}
      borderRadius={DEFAULT_BORDER_RADIUS}
      justify="end"
      bgColor="bg.white"
      {...props}
    >
      <Center
        pos="absolute"
        top={{ laptop: '12px', mobile: '8px' }}
        left={{ laptop: '12px', mobile: '8px' }}
        boxSize={{ laptop: '72px', mobile: '40px' }}
        p={{ laptop: '10px', mobile: '8px' }}
        bgColor="bg.white"
        borderRadius="50%"
        backgroundImage={companyIcon.src}
        bgRepeat="no-repeat"
        bgPos="center"
        bgSize="cover"
        borderWidth={iconBorder ? '1.6px' : 0}
        borderStyle="solid"
        borderColor="text.black"
        boxSizing="border-box"
        overflow="hidden"
        className="review-icon"
        {...iconProps}
      />

      <VStack w="full" align="start" gap="32px" {...textsContainerProps}>
        <VStack w="full" align="start" gap={{ mobile: '12px', laptop: '16px' }}>
          <AnimatedText
            as="h3"
            textStyle="header.h4"
            fontSize={{ mobile: '18px', laptop: '24px', desktop: '20px' }}
            lineHeight={{ mobile: '120%', laptop: '140%', desktop: '120%' }}
            letterSpacing={{ mobile: '-0.03em', laptop: '0em', desktop: '-0.03em' }}
            condition={isViewed}
            duration={800}
            {...textProps}
          >
            &laquo;{text}&raquo;.
          </AnimatedText>

          {pdfLink && (
            <HStack align="start" pos="relative" gap={{ mobile: '8px', laptop: '12px' }}>
              <CustomOverlayLink
                link={`/recommendationLetters/${pdfLink}`}
                title={reviewsPdfLinkText}
                isExternal
              />

              <NextImage
                src={Pdf}
                alt="Иконка PDF"
                w="auto"
                h="auto"
                boxSize={{ mobile: '16px', laptop: '24px' }}
              />

              <Text
                textStyle="button.secondary"
                textDecoration="underline"
                textUnderlineOffset="0.25em"
                _after={{ content: `'.'`, color: 'bg.accent' }}
              >
                {reviewsPdfLinkText}
              </Text>
            </HStack>
          )}
        </VStack>

        <VStack w="full" align="start" gap={{ mobile: '4px', laptop: '8px' }}>
          <Text
            fontSize={{ mobile: '18px', laptop: '24px', desktop: '20px' }}
            lineHeight={{ mobile: '120%', laptop: '140%' }}
            fontWeight="500"
            letterSpacing={{ mobile: '-0.03em', laptop: '0em' }}
            {...nameProps}
          >
            {name}
          </Text>

          <Text textStyle="h2" color="text.steelGray" {...positionProps}>
            {position}
          </Text>
        </VStack>
      </VStack>
    </VStack>
  );
};

export default memo(ReviewCard);
